import { useCallback, useRef, useState, type MutableRefObject, type RefObject } from 'react';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { useAppStore } from '../../../../store/appStore';
import { executeQueryV2 } from '../../../database-objects/services/queryExecutor';
import { createExecutionId } from '../../../../types/queryExecution';
import { isAnonymousPlsqlBlock } from '@/core/engines/oraclePlsqlDetection';
import type { QueryMessage } from '../../core/types/workspaceTypes';
import type { DatabaseEngineId } from '../../../../core/engines';
import type { QueryEditorHandle } from '../components/QueryEditor';
import { readLiveQuery } from '../components/liveQuery';
import { safeConnectionServer } from '@/types/safeConnectionDisplay';

type UsePlanAndDebugActionsParams = {
  tabId: string;
  engineId: DatabaseEngineId;
  connectionId: string | undefined;
  database: string | undefined;
  connectionInfo: { connectionName?: string; database?: string } | undefined;
  /** Debounced React query state (PL/SQL detection + debug source fallback). */
  query: string;
  editorRef: RefObject<QueryEditorHandle | null>;
  queryRef: MutableRefObject<string>;
  /** Focuses the results-pane tab after a plan run ('plan' / 'messages'). */
  setActiveTab: (tab: string) => void;
};

/**
 * Estimated-execution-plan capture and PL/SQL debug launch for a query editor
 * tab. `handleExplainPlan` runs the selection (else the live buffer) with
 * `plan: { mode: 'estimated' }`, appends progress/success/error messages to
 * the tab, and focuses the plan/messages tab; `handleDebugPlsql` opens the
 * PL/SQL debugger workspace seeded with the buffer (Oracle only). Behavior
 * carried verbatim from the previous inline implementation.
 */
export function usePlanAndDebugActions({
  tabId,
  engineId,
  connectionId,
  database: tabDatabase,
  connectionInfo,
  query,
  editorRef,
  queryRef,
  setActiveTab,
}: UsePlanAndDebugActionsParams) {
  const updateTabContent = useWorkspaceStore((state) => state.updateTabContent);

  const [isExplainingPlan, setIsExplainingPlan] = useState(false);
  const explainRunNumberRef = useRef(0);

  // PL/SQL Debug support (Oracle only)
  const isOracle = engineId === 'oracle';
  // U5 — the ONE anonymous-PL/SQL predicate, shared with the adapter and the
  // debugger's own wrapper, so the Debug button appears only for blocks the
  // debugger can actually run.
  const isPlsql = isOracle && isAnonymousPlsqlBlock(query);

  const handleDebugPlsql = useCallback(() => {
    if (!connectionId || !isOracle) return;
    const plsqlSource = readLiveQuery(editorRef.current, query, 'Debug cancelled');
    if (plsqlSource === null || !plsqlSource.trim()) return;

    const activeConn = useConnectionStore.getState().activeConnections.find((c) => c.id === connectionId);
    const database = connectionInfo?.database || activeConn?.connection.database || '';

    const { addTab } = useWorkspaceStore.getState();
    addTab({
      title: 'Debug: Anonymous Block',
      type: 'pl-sql-debugger',
      content: {
        objectName: 'Anonymous Block',
        schema: '',
        database,
        objectType: 'anonymous',
        source: plsqlSource,
        debugState: 'idle',
        breakpoints: [],
        breakpointsByProgram: {},
        variables: [],
        callStack: [],
        messages: [],
        watchExpressions: [],
        exceptionBreakpoints: { breakOnAll: false, namedExceptions: [] },
        isLoading: false,
      },
      connectionInfo: {
        connectionId,
        connectionName: connectionInfo?.connectionName || 'Unnamed Connection',
        serverHost: safeConnectionServer(activeConn?.connection),
        database,
      },
    });
  }, [connectionId, isOracle, query, connectionInfo, editorRef]);

  const createMessage = useCallback((type: QueryMessage['type'], content: string): QueryMessage => ({
    id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    type,
    content,
    timestamp: new Date(),
  }), []);

  const handleExplainPlan = useCallback(async () => {
    if (!connectionId) return;
    const selectedText = editorRef.current?.getSelectedText() || '';
    const editorQuery = readLiveQuery(editorRef.current, queryRef.current, 'Explain cancelled');
    if (editorQuery === null) return;
    // An MCP execute_with_plan queued at tab creation runs before Monaco has
    // loaded the buffer — the mounted model is '' while the tab's own copy
    // already holds the script, so fall back to queryRef whenever the editor
    // text is still empty.
    const liveQuery = editorQuery.trim() ? editorQuery : queryRef.current;
    const textToExplain = selectedText.trim() ? selectedText : liveQuery;
    if (!textToExplain.trim()) return;

    setIsExplainingPlan(true);

    explainRunNumberRef.current += 1;
    const executionId = createExecutionId(`${tabId}-plan`, explainRunNumberRef.current);

    const currentTabState = useWorkspaceStore.getState().tabs.find((tab) => tab.id === tabId);
    const existingMessages = (currentTabState?.type === 'query' ? currentTabState.content?.messages : []) || [];
    const planTabContent = currentTabState?.type === 'query' ? currentTabState.content : undefined;
    // The run this capture is happening ON TOP of. Both values are needed
    // below: the start to recognise it later, the end because a capture may
    // already have bounded it.
    const runStartAtCapture = planTabContent?.messagesRunStart;
    const runEndAtCapture = planTabContent?.messagesRunEnd;
    const startMessage = createMessage('info', 'Capturing estimated execution plan...');
    // A plan capture is not a RUN: the tab still displays the previous query's
    // result sets, so its Output view must keep describing THAT query. Pin the
    // run's END here — BEFORE this first line is appended — and carry the same
    // bound through every write below, so none of the capture's own lines
    // (this one included; `isUserPrintMessage` would otherwise read it as user
    // output and raise the Output tab) joins the finished query's window.
    // `messagesRunStart` is deliberately untouched.
    //
    // An EXISTING bound wins: on a second Explain the log already carries the
    // first capture's lines, so measuring afresh would stretch the window to
    // swallow them.
    const runEndBeforePlan = runEndAtCapture ?? existingMessages.length;

    /**
     * The last line the log carried when this capture began. Used to tell
     * "still the same run" from "a run finished while the plan was in flight":
     * `messagesRunStart` alone cannot, because plaintext mode resets the log
     * every run and so pins the start at 0 for all of them.
     */
    const anchorMessageId = existingMessages[existingMessages.length - 1]?.id;

    /**
     * The bound to write AFTER the await.
     *
     * Same run → restore the bound measured before the capture. DIFFERENT run
     * (one finished while the plan was in flight, setting its own start and
     * clearing the end) → bound that run at the log length as it stands now,
     * so the capture's own lines stay outside ITS window too. What must never
     * happen is writing the pre-capture length onto a newer run: that leaves
     * an end below its start and blanks its Output.
     */
    const planBound = (logLengthNow: number): { messagesRunEnd: number } => {
      const tabNow = useWorkspaceStore.getState().tabs.find((tab) => tab.id === tabId);
      const contentNow = tabNow?.type === 'query' ? tabNow.content : undefined;
      const messagesNow = contentNow?.messages ?? [];
      const sameRun =
        contentNow?.messagesRunStart === runStartAtCapture
        && (anchorMessageId === undefined
          || messagesNow[runEndBeforePlan - 1]?.id === anchorMessageId);
      return { messagesRunEnd: sameRun ? runEndBeforePlan : logLengthNow };
    };
    updateTabContent(tabId, {
      messages: [...existingMessages, startMessage],
      messagesRunEnd: runEndBeforePlan,
    });

    try {
      const result = await executeQueryV2({
        connectionId,
        sql: textToExplain,
        database: tabDatabase,
        executionId,
        options: {
          plan: { mode: 'estimated' },
          timeoutMs: useAppStore.getState().queryExecutionSettings.timeoutSeconds * 1000,
        },
      });

      const freshTab = useWorkspaceStore.getState().tabs.find((tab) => tab.id === tabId);
      const latestMessages = (freshTab?.type === 'query' ? freshTab.content?.messages : []) || [];
      // `EXPLAIN` without ANALYZE still evaluates an immutable function at plan
      // time, so PostgreSQL can raise a notice here; the adapter returns it and
      // this is the only place that can show it. Placed before the outcome
      // line, as the execution path does.
      const capturedMessages = (result.messages ?? []).map((msg) =>
        createMessage(msg.type === 'error' ? 'error' : msg.type === 'warning' ? 'warning' : 'info', msg.text)
      );

      if (result.success && result.plan) {
        const nextMessages = [
          ...latestMessages,
          ...capturedMessages,
          createMessage('success', 'Estimated execution plan captured.'),
        ];
        updateTabContent(tabId, {
          executionPlan: result.plan,
          messages: nextMessages,
          ...planBound(latestMessages.length),
        });
        setActiveTab('plan');
        return;
      }

      const errorText = result.error?.message || 'Failed to capture estimated execution plan.';
      updateTabContent(tabId, {
        messages: [...latestMessages, ...capturedMessages, createMessage('error', errorText)],
        ...planBound(latestMessages.length),
      });
      setActiveTab('messages');
    } catch (error) {
      const errorText = error instanceof Error ? error.message : 'Failed to capture estimated execution plan.';
      const freshTab = useWorkspaceStore.getState().tabs.find((tab) => tab.id === tabId);
      const latestMessages = (freshTab?.type === 'query' ? freshTab.content?.messages : []) || [];
      updateTabContent(tabId, {
        messages: [...latestMessages, createMessage('error', errorText)],
        ...planBound(latestMessages.length),
      });
      setActiveTab('messages');
    } finally {
      setIsExplainingPlan(false);
    }
  }, [connectionId, createMessage, tabDatabase, tabId, updateTabContent, queryRef, editorRef]);

  return {
    isExplainingPlan,
    isOracle,
    isPlsql,
    handleDebugPlsql,
    handleExplainPlan,
  };
}
